import { useEffect, useMemo, useRef, useState } from "react";
import {
  redeemOffer,
  getAllOffers,
  getOffersContact,
  getOfferRegions,
  lookupContactByGhin,
} from "../../api/api-hook";
import { goToPurchasePage, openPurchasePageInNewTab } from "../../utils/purchasePage";
import { useLocation, useNavigate } from "react-router-dom";
import { getAdminSession } from "../../utils/adminAuth";

/**  UPDATED: include region + lat/lng */
type Company = {
  id: string;
  name: string;
  clubId?: string;
  city?: string;
  state?: string;
  phone?: string;
  region?: string;

  //  HubSpot company properties (you said you have 2 props)
  latitude?: number | null;
  longitude?: number | null;
};

type OfferApiItem =
  | string
  | {
      offerText?: string;
      text?: string;
      number_of_plays?: number;
      numberOfPlays?: number;
      vipRedeemedCount?: number;
      redeemedCount?: number;
      vipRedeemedLastAt?: string | null;
      unlimited?: boolean;

      days_of_the_week?: string | null;
      months?: string | null;

      // ✅ HubDB column: number_of_days (ex: "1-31")
      number_of_days?: string | null;

      region?: string | null;

      // ✅ exclusive group flags from backend
      groupId?: string | null;
      groupRedeemedOfferKey?: string | null;
      groupIsRedeemedOffer?: boolean;
      groupIsUnavailable?: boolean;
      groupRedeemedAt?: string | null;
    };

type Card = {
  hubdbRowId: string;
  clubId: string;
  company: Company;
  offers: OfferApiItem[];
  redemption?: {
    used: number;
    totalAllowed: number | null;
    remaining: number | null;
    unlimited?: boolean;
  };

  vipRedeemed?: boolean;
  vipRedeemedCount?: number;
  vipRedeemedLastAt?: string | null;
  groupId?: string | null;
};

type OfferItem = {
  hubdbRowId: string;
  clubId: string;
  company: Company;
  offerNumber: number;
  offerText: string;

  numberOfPlays?: number;
  vipRedeemedCount?: number;
  vipRedeemedLastAt?: string | null;

  unlimited?: boolean;

  days_of_the_week?: string | null;
  months?: string | null;

  // ✅ HubDB column: number_of_days (ex: "1-31")
  number_of_days?: string | null;

  //  computed distance (miles) when user location + company lat/lng exist
  distanceMiles?: number | null;

  redemption?: {
    used: number;
    totalAllowed: number | null;
    remaining: number | null;
    unlimited?: boolean;
  };

  vipRedeemed?: boolean;
  groupId?: string | null;

  // ✅ exclusive group flags carried to UI
  groupRedeemedOfferKey?: string | null;
  groupIsRedeemedOffer?: boolean;
  groupIsUnavailable?: boolean;
  groupRedeemedAt?: string | null;
};

type AllOffersResp = {
  success: boolean;
  results: Card[];
  message?: string;
};

type ContactResp = {
  success: boolean;
  contact?: {
    id: string;
    fullName: string;
    vipCardNumber: string;
    membership?: {
      seasons: string[];          // seasons whose cards can still be used, oldest first
      expiresOn: string | null;   // when the member's latest card stops redeeming offers
      renewSeason: string | null; // next season's card, when the member can buy it now
    };
  };
  message?: string;
};

const formatLongDate = (iso: string) => {
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(y, m - 1, d).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
};

type LocationState = { contactId?: string };

const OffersList = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const { contactId: locationContactId } = (location.state || {}) as LocationState;

  // App mode: detect GHIN + lastName query params AND mobile app user agent
  const searchParams = new URLSearchParams(location.search);
  const ghinNumber = searchParams.get("ghinNumber") || "";
  const queryLastName = searchParams.get("lastName") || "";
  const isAppMode = !!(
    ghinNumber &&
    queryLastName &&
    navigator.userAgent.includes("org.vsga.myvsga")
  );

  const [appContactId, setAppContactId] = useState<string | null>(null);

  // Admin preview: an admin opens /offers?previewContact=<contactId> from a golfer's record to see the
  // page as that golfer does, without their login. It needs an admin session, and redeeming is off.
  const previewContactId = searchParams.get("previewContact") || "";
  const isAdminPreview = !!previewContactId && !!getAdminSession();

  // ?redeemed=1 narrows the list to offers this golfer has redeemed this year
  const onlyRedeemed = searchParams.get("redeemed") === "1";
  const setOnlyRedeemed = (on: boolean) => {
    const params = new URLSearchParams(location.search);
    if (on) params.set("redeemed", "1");
    else params.delete("redeemed");
    const qs = params.toString();
    navigate(`${location.pathname}${qs ? `?${qs}` : ""}`, { replace: true, state: location.state });
  };

  // Unified contactId: admin preview, navigation state (OTP login), or app mode lookup
  const contactId = (isAdminPreview ? previewContactId : "") || locationContactId || appContactId || undefined;

  const goLogin = () => navigate("/offers/login");

  const [contact, setContact] = useState<ContactResp["contact"] | null>(null);
  const [loadingContact, setLoadingContact] = useState(false);

  const [companies, setCompanies] = useState<Company[]>([]);
  const [loadingCompanies, setLoadingCompanies] = useState(false);

  const [regions, setRegions] = useState<string[]>([]);
  const [selectedRegion, setSelectedRegion] = useState<string>("");

  const [allCards, setAllCards] = useState<Card[]>([]);
  const [loadingOffers, setLoadingOffers] = useState(false);

  const [companyId, setCompanyId] = useState<string>("");

  //  Custom date picker state (ISO yyyy-mm-dd)
  const [selectedDate, setSelectedDate] = useState<string>("");

  const [redeemingKey, setRedeemingKey] = useState<string | null>(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [selectedOffer, setSelectedOffer] = useState<OfferItem | null>(null);

  const [error, setError] = useState<string | null>(null);

  //  Tabs
  const [activeTab, setActiveTab] = useState<"all" | "nearby">("all");

  //  User geolocation (for Nearby tab)
  const [userCoords, setUserCoords] = useState<{ lat: number; lng: number } | null>(
    null
  );
  const [geoLoading, setGeoLoading] = useState(false);
  const [geoError, setGeoError] = useState<string | null>(null);

  const parseMaybeJson = <T,>(x: any): T =>
    typeof x === "string" ? (JSON.parse(x) as T) : (x as T);

  const isLoggedIn = !!contactId && !!contact;

  const modalBusy =
    !!redeemingKey &&
    !!selectedOffer &&
    redeemingKey === `${selectedOffer.hubdbRowId}-${selectedOffer.offerNumber}`;

  const norm = (s?: string) =>
    String(s ?? "")
      .trim()
      .toLowerCase()
      .replace(/\s+/g, " ");

  const splitCsv = (s?: string | null) =>
    String(s ?? "")
      .split(",")
      .map((x) => x.trim())
      .filter(Boolean);

  const MONTH_TO_NUMBER: Record<string, number> = {
    january: 1,
    february: 2,
    march: 3,
    april: 4,
    may: 5,
    june: 6,
    july: 7,
    august: 8,
    september: 9,
    october: 10,
    november: 11,
    december: 12,
  };

  const DAY_TO_INDEX: Record<string, number> = {
    sunday: 0,
    monday: 1,
    tuesday: 2,
    wednesday: 3,
    thursday: 4,
    friday: 5,
    saturday: 6,
  };

  /* ------------------------------------------------------------------ */
  /*  ✅ Month + number_of_days window logic                             */
  /* ------------------------------------------------------------------ */

  const clampDayToMonth = (year: number, monthNum: number, day: number) => {
    const lastDay = new Date(year, monthNum, 0).getDate(); // last day of monthNum
    return Math.max(1, Math.min(day, lastDay));
  };

  const parseNumberOfDaysRange = (raw?: string | null) => {
    const s = String(raw ?? "").trim();
    if (!s) return null;

    // Supports: "1-31", "1 – 31", "1 to 31", "15"
    const normalized = s
      .toLowerCase()
      .replace(/to/g, "-")
      .replace(/[–—]/g, "-")
      .replace(/\s+/g, "");

    const parts = normalized.split("-").filter(Boolean);

    const start = Number(parts[0]);
    const end = parts.length > 1 ? Number(parts[1]) : start;

    if (!Number.isFinite(start) || !Number.isFinite(end)) return null;

    const startDay = Math.max(1, Math.min(31, Math.trunc(start)));
    const endDay = Math.max(1, Math.min(31, Math.trunc(end)));

    return { startDay, endDay };
  };

  /**
   * Builds an inclusive date window from:
   * - months: "January, February, March"
   * - number_of_days: "1-31"
   *
   * Example: Jan 1 -> Mar 31 (for the anchor year)
   * Supports wrap: "November, December, January" -> Nov 1 -> Jan 31 (end year +1)
   */
  const getMonthDayWindow = (
    monthsRaw?: string | null,
    numberOfDaysRaw?: string | null,
    anchorDate?: Date
  ) => {
    const months = splitCsv(monthsRaw)
      .map((m) => MONTH_TO_NUMBER[String(m).toLowerCase()])
      .filter((n): n is number => typeof n === "number" && Number.isFinite(n));

    const dayRange = parseNumberOfDaysRange(numberOfDaysRaw);

    if (months.length === 0 || !dayRange) return null;

    const anchorYear = (anchorDate ?? new Date()).getFullYear();

    // Order-insensitive: use min/max month numbers as span endpoints
    const sortedMonths = [...months].sort((a, b) => a - b);
    const startMonth = sortedMonths[0];
    const endMonth = sortedMonths[sortedMonths.length - 1];

    // wrap season if endMonth < startMonth (ex: Nov..Jan)
    const wraps = endMonth < startMonth;

    const startYear = anchorYear;
    const endYear = wraps ? anchorYear + 1 : anchorYear;

    const startDay = clampDayToMonth(startYear, startMonth, dayRange.startDay);
    const endDay = clampDayToMonth(endYear, endMonth, dayRange.endDay);

    const start = new Date(startYear, startMonth - 1, startDay, 0, 0, 0, 0);
    const end = new Date(endYear, endMonth - 1, endDay, 23, 59, 59, 999);

    return { start, end };
  };

  const isWithinWindow = (date: Date, win: { start: Date; end: Date }) => {
    const t = date.getTime();
    return t >= win.start.getTime() && t <= win.end.getTime();
  };

  const isOfferValidForDate = (offer?: OfferItem, dateStr?: string) => {
    if (!offer) return true;
    if (!dateStr) return true;

    const d = new Date(`${dateStr}T00:00:00`);
    if (Number.isNaN(d.getTime())) return true;

    // ✅ Use months + number_of_days window when present
    const win = getMonthDayWindow(offer.months, offer.number_of_days, d);
    if (win) return isWithinWindow(d, win);

    // fallback to month-only if window data missing
    const monthNum = d.getMonth() + 1;
    const allowedMonths = splitCsv(offer.months);
    if (allowedMonths.length === 0) return true;

    const allowedMonthNums = allowedMonths
      .map((m) => MONTH_TO_NUMBER[String(m).toLowerCase()])
      .filter((n) => typeof n === "number" && !Number.isNaN(n));

    if (allowedMonthNums.length === 0) return true;
    return allowedMonthNums.includes(monthNum);
  };

  const getOfferValidityNow = (item?: OfferItem) => {
    if (!item)
      return {
        isValidNow: true,
        dayOk: true,
        monthOk: true,
        windowOk: true,
        allowedDays: [] as string[],
        allowedMonths: [] as string[],
      };

    const now = new Date();
    const todayIdx = now.getDay();
    const thisMonthNum = now.getMonth() + 1;

    const allowedDays = splitCsv(item.days_of_the_week);
    const allowedMonths = splitCsv(item.months);

    const dayOk =
      allowedDays.length === 0
        ? true
        : allowedDays
            .map((d) => DAY_TO_INDEX[String(d).toLowerCase()])
            .filter((n) => typeof n === "number" && !Number.isNaN(n))
            .includes(todayIdx);

    // ✅ Window validity for months + number_of_days
    const win = getMonthDayWindow(item.months, item.number_of_days, now);
    const windowOk = win ? isWithinWindow(now, win) : true;

    // monthOk kept for display/debug
    const monthOk =
      allowedMonths.length === 0
        ? true
        : allowedMonths
            .map((m) => MONTH_TO_NUMBER[String(m).toLowerCase()])
            .filter((n) => typeof n === "number" && !Number.isNaN(n))
            .includes(thisMonthNum);

    return {
      isValidNow: dayOk && windowOk,
      dayOk,
      monthOk,
      windowOk,
      allowedDays,
      allowedMonths,
    };
  };

  const formatUSPhone = (phone?: string) => {
    if (!phone) return "";
    const digits = phone.replace(/\D/g, "");
    if (digits.length === 10) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    }
    if (digits.length === 11 && digits.startsWith("1")) {
      const d = digits.slice(1);
      return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
    }
    return phone;
  };

  // Clubs in HubSpot have no state set yet; every offer region is in Virginia or West Virginia.
  const STATE_CODES: Record<string, string> = { virginia: "VA", "west virginia": "WV" };
  const stateCode = (c?: Company) => {
    const raw = (c?.state || "").trim();
    if (raw) return STATE_CODES[raw.toLowerCase()] || raw;
    // Exact match: "Southwest Virginia" also contains the words "west virginia" but is in Virginia.
    return /^\s*west virginia\s*$/i.test(c?.region || "") ? "WV" : "VA";
  };

  const formatLocation = (c?: Company) => {
    const city = (c?.city || "").trim();
    const state = stateCode(c);
    if (city && state) return `${city}, ${state}`;
    return city || state || "";
  };

  /**
   * ✅ FIXED:
   * - DO NOT treat groupIsRedeemedOffer as "fully redeemed" (it just means it's the selected offer in the group)
   * - Remaining plays should still be based on used vs allowed
   */
  const getOfferPlayStats = (item?: OfferItem) => {
    const isUnlimited = Boolean(item?.unlimited);

    const used =
      Number((item as any)?.vipRedeemedCount ?? 0) ||
      Number((item as any)?.redeemedCount ?? 0) ||
      Number(item?.redemption?.used ?? 0) ||
      0;

    if (isUnlimited) {
      return {
        isUnlimited: true as const,
        allowed: null as number | null,
        used,
        remaining: null as number | null,
      };
    }

    const playsFromOffer = Number(item?.numberOfPlays ?? 0);
    const playsFromCard = Number(item?.redemption?.totalAllowed ?? 0);

    const allowed =
      playsFromOffer > 0 ? playsFromOffer : playsFromCard > 0 ? playsFromCard : 1;

    const remaining = Math.max(0, allowed - used);

    return { isUnlimited: false as const, allowed, used, remaining };
  };

  /* ------------------------------------------------------------------ */
  /*  Distance helpers                                                  */
  /* ------------------------------------------------------------------ */

  const HUBSPOT_LAT_KEYS = ["latitude", "lat", "company_latitude", "hs_latitude"];
  const HUBSPOT_LNG_KEYS = [
    "longitude",
    "lng",
    "lon",
    "company_longitude",
    "hs_longitude",
  ];

  const pickNumber = (obj: any, keys: string[]) => {
    for (const k of keys) {
      const v = obj?.[k];
      if (v == null) continue;
      const n = typeof v === "number" ? v : Number(String(v).trim());
      if (!Number.isNaN(n) && Number.isFinite(n)) return n;
    }
    return null;
  };

  const haversineMiles = (lat1: number, lon1: number, lat2: number, lon2: number) => {
    const toRad = (x: number) => (x * Math.PI) / 180;
    const R = 3958.7613; // miles
    const dLat = toRad(lat2 - lat1);
    const dLon = toRad(lon2 - lon1);
    const a =
      Math.sin(dLat / 2) ** 2 +
      Math.cos(toRad(lat1)) *
        Math.cos(toRad(lat2)) *
        Math.sin(dLon / 2) ** 2;
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  };

  const requestLocation = async () => {
    setGeoError(null);

    if (!window.isSecureContext) {
      setGeoError(
        "Location requires a secure site (HTTPS). If you’re on HTTP, switch to HTTPS to enable Nearby sorting."
      );
      setUserCoords(null);
      return;
    }

    if (!("geolocation" in navigator)) {
      setGeoError("Geolocation isn’t supported in this browser.");
      setUserCoords(null);
      return;
    }

    setGeoLoading(true);

    try {
      await new Promise<void>((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(
          (pos) => {
            setUserCoords({
              lat: pos.coords.latitude,
              lng: pos.coords.longitude,
            });
            resolve();
          },
          (err) => reject(err),
          {
            enableHighAccuracy: true,
            timeout: 15000,
            maximumAge: 0,
          }
        );
      });
    } catch (e: any) {
      const code = e?.code;
      const msg =
        code === 1
          ? "Location permission denied. Enable location for this site in browser settings."
          : code === 2
          ? "Location unavailable. Your device couldn’t determine a position."
          : code === 3
          ? "Location request timed out. Try again."
          : `Couldn’t get your location. (${String(e?.message || "unknown error")})`;

      setGeoError(msg);
      setUserCoords(null);
      console.log("geolocation error:", e);
    } finally {
      setGeoLoading(false);
    }
  };

  /* ------------------------------------------------------------------ */
  /*  Custom Date Picker (works in all browsers)                        */
  /* ------------------------------------------------------------------ */

  const [dateOpen, setDateOpen] = useState(false);
  const [calAnchorMonth, setCalAnchorMonth] = useState(() => {
    const d = new Date();
    d.setDate(1);
    d.setHours(0, 0, 0, 0);
    return d;
  });

  const dateWrapRef = useRef<HTMLDivElement | null>(null);

  const toISODate = (d: Date) => {
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  };

  const prettyDate = (iso?: string) => {
    if (!iso) return "";
    const d = new Date(`${iso}T00:00:00`);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "2-digit",
    });
  };

  const openCalendar = () => {
    setError(null);
    setDateOpen(true);

    if (selectedDate) {
      const d = new Date(`${selectedDate}T00:00:00`);
      if (!Number.isNaN(d.getTime())) {
        const a = new Date(d);
        a.setDate(1);
        a.setHours(0, 0, 0, 0);
        setCalAnchorMonth(a);
      }
    } else {
      const now = new Date();
      now.setDate(1);
      now.setHours(0, 0, 0, 0);
      setCalAnchorMonth(now);
    }
  };

  const closeCalendar = () => setDateOpen(false);

  useEffect(() => {
    const onDocDown = (e: MouseEvent) => {
      if (!dateOpen) return;
      const wrap = dateWrapRef.current;
      if (!wrap) return;
      if (wrap.contains(e.target as Node)) return;
      closeCalendar();
    };

    const onEsc = (e: KeyboardEvent) => {
      if (!dateOpen) return;
      if (e.key === "Escape") closeCalendar();
    };

    document.addEventListener("mousedown", onDocDown);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDocDown);
      document.removeEventListener("keydown", onEsc);
    };
  }, [dateOpen]);

  const buildCalendarDays = (anchorMonth: Date) => {
    const first = new Date(anchorMonth);
    first.setDate(1);
    const startDay = first.getDay();
    const gridStart = new Date(first);
    gridStart.setDate(first.getDate() - startDay);

    const days: Date[] = [];
    for (let i = 0; i < 42; i++) {
      const d = new Date(gridStart);
      d.setDate(gridStart.getDate() + i);
      d.setHours(0, 0, 0, 0);
      days.push(d);
    }
    return days;
  };

  const calendarDays = useMemo(() => buildCalendarDays(calAnchorMonth), [calAnchorMonth]);

  const isSameDay = (a: Date, iso?: string) => {
    if (!iso) return false;
    const b = new Date(`${iso}T00:00:00`);
    if (Number.isNaN(b.getTime())) return false;
    return (
      a.getFullYear() === b.getFullYear() &&
      a.getMonth() === b.getMonth() &&
      a.getDate() === b.getDate()
    );
  };

  const monthLabel = useMemo(() => {
    return calAnchorMonth.toLocaleDateString(undefined, { month: "long", year: "numeric" });
  }, [calAnchorMonth]);

  const prevMonth = () => {
    const d = new Date(calAnchorMonth);
    d.setMonth(d.getMonth() - 1);
    d.setDate(1);
    setCalAnchorMonth(d);
  };

  const nextMonth = () => {
    const d = new Date(calAnchorMonth);
    d.setMonth(d.getMonth() + 1);
    d.setDate(1);
    setCalAnchorMonth(d);
  };

  const pickDate = (d: Date) => {
    const iso = toISODate(d);
    setSelectedDate(iso);
    closeCalendar();
  };

  /* ------------------------------------------------------------------ */
  /* Data loading                                                        */
  /* ------------------------------------------------------------------ */

  useEffect(() => {
    const loadContact = async () => {
      if (!contactId) return;
      setLoadingContact(true);
      try {
        const resp = await getOffersContact(contactId);
        const data = parseMaybeJson<ContactResp>(resp);
        if (data?.success && data.contact) setContact(data.contact);
      } catch (e) {
        console.error(e);
      } finally {
        setLoadingContact(false);
      }
    };
    loadContact();
  }, [contactId]);

  // App mode: auto-lookup contact by GHIN + lastName (no OTP)
  useEffect(() => {
    if (!isAppMode || appContactId) return;

    const lookupByGhin = async () => {
      setLoadingContact(true);
      try {
        const resp = await lookupContactByGhin(ghinNumber, queryLastName);
        const data = parseMaybeJson<ContactResp>(resp);
        if (data?.success && data.contact) {
          setContact(data.contact);
          setAppContactId(data.contact.id);
        } else {
          setError(data?.message || "Member not found. Please check your details.");
        }
      } catch (e) {
        console.error(e);
        setError("Failed to load member. Please try again.");
      } finally {
        setLoadingContact(false);
      }
    };

    lookupByGhin();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAppMode]);

  useEffect(() => {
    const loadFilters = async () => {
      setLoadingCompanies(true);
      setError(null);

      try {
        const resp = await getOfferRegions();
        const data = parseMaybeJson<any>(resp);

        if (!data?.success) {
          setError(data?.message || "Failed to load filters.");
          setCompanies([]);
          setRegions([]);
          return;
        }

        const clubs: Company[] = Array.isArray(data.clubs)
          ? data.clubs.map((c: any) => ({
              id: "",
              clubId: String(c.clubId),
              name: String(c.name || "").trim(),
              region: String(c.region || "").trim(),
            }))
          : [];

        setCompanies(clubs);

        if (Array.isArray(data.regions)) {
          const cleaned: string[] = data.regions
            .map((r: unknown) => String(r ?? "").trim())
            .filter((r: string) => r.length > 0);

          const uniqueSorted: string[] = Array.from(new Set<string>(cleaned)).sort((a, b) =>
            a.localeCompare(b)
          );

          setRegions(uniqueSorted);
        } else {
          const derived = Array.from(
            new Set(
              clubs.map((c) => String(c.region || "").trim()).filter((r) => r.length > 0)
            )
          ).sort((a, b) => a.localeCompare(b));
          setRegions(derived);
        }
      } catch (e) {
        console.error(e);
        setError("Failed to load filters. Please try again.");
        setCompanies([]);
        setRegions([]);
      } finally {
        setLoadingCompanies(false);
      }
    };

    loadFilters();
  }, []);

  useEffect(() => {
    const loadAllOffers = async () => {
      setLoadingOffers(true);
      setError(null);

      try {
        const vip = String(contact?.vipCardNumber || "").trim();
        const resp = await getAllOffers(vip || undefined);
        const data = parseMaybeJson<AllOffersResp>(resp);

        if (!data?.success) {
          setError(data?.message || "Failed to load offers.");
          setAllCards([]);
          return;
        }

        setAllCards(data.results || []);
      } catch (e) {
        console.error(e);
        setError("Failed to load offers. Please try again.");
        setAllCards([]);
      } finally {
        setLoadingOffers(false);
      }
    };

    loadAllOffers();
  }, [contact?.vipCardNumber]);

  // Flatten (+ map lat/lng from HubSpot props)
  const offerItems = useMemo<OfferItem[]>(() => {
    return allCards.flatMap((card) => {
      const offers = Array.isArray(card.offers) ? card.offers : [];
      const groupId =
        (card as any).groupId == null ? null : String((card as any).groupId).trim() || null;

      const companyRaw: any = card.company || {};

      const latitude = pickNumber(companyRaw, HUBSPOT_LAT_KEYS);
      const longitude = pickNumber(companyRaw, HUBSPOT_LNG_KEYS);

      const baseCompany: Company = {
        id: String(companyRaw?.id || "").trim(),
        name: String(companyRaw?.name || "").trim(),
        clubId: String(card.clubId || "").trim(),
        city: String(companyRaw?.city || "").trim(),
        state: String(companyRaw?.state || "").trim(),
        phone: String(companyRaw?.phone || "").trim(),
        region: String((companyRaw as any)?.region || "").trim(),
        latitude,
        longitude,
      };

      return offers.map((o, idx) => {
        const isObj = o && typeof o === "object";

        const offerText =
          typeof o === "string" ? o : String((o as any).offerText ?? (o as any).text ?? "").trim();

        const numberOfPlaysRaw =
          isObj ? (o as any).number_of_plays ?? (o as any).numberOfPlays : undefined;

        const numberOfPlays =
          numberOfPlaysRaw == null
            ? card.redemption?.totalAllowed == null
              ? undefined
              : Number(card.redemption?.totalAllowed || 0) || undefined
            : Number(numberOfPlaysRaw);

        const usedCountRaw =
          isObj ? (o as any).vipRedeemedCount ?? (o as any).redeemedCount : undefined;

        const usedCount =
          usedCountRaw == null
            ? Number(card.vipRedeemedCount ?? card.redemption?.used ?? 0)
            : Number(usedCountRaw);

        const unlimited = isObj ? Boolean((o as any).unlimited) : false;

        const days_of_the_week = isObj ? ((o as any).days_of_the_week ?? null) : null;
        const months = isObj ? ((o as any).months ?? null) : null;

        // ✅ NEW: HubDB column value
        const number_of_days = isObj ? ((o as any).number_of_days ?? null) : null;

        const offerRegion = isObj ? String((o as any).region ?? "").trim() : "";
        const region = offerRegion || baseCompany.region || "";

        // group lock flags from backend per offer
        const groupRedeemedOfferKey = isObj ? ((o as any).groupRedeemedOfferKey ?? null) : null;
        const groupIsRedeemedOffer = isObj ? Boolean((o as any).groupIsRedeemedOffer) : false;
        const groupIsUnavailable = isObj ? Boolean((o as any).groupIsUnavailable) : false;
        const groupRedeemedAt = isObj ? ((o as any).groupRedeemedAt ?? null) : null;

        return {
          hubdbRowId: String(card.hubdbRowId || "").trim(),
          clubId: String(card.clubId || "").trim(),
          company: { ...baseCompany, region },

          groupId,
          groupRedeemedOfferKey,
          groupIsRedeemedOffer,
          groupIsUnavailable,
          groupRedeemedAt,

          offerNumber: idx + 1,
          offerText,

          numberOfPlays,

          // ✅ IMPORTANT: vipRedeemedCount stays as "used plays", not "group selection"
          vipRedeemedCount: usedCount,

          vipRedeemedLastAt: isObj
            ? ((o as any).vipRedeemedLastAt ?? card.vipRedeemedLastAt ?? null)
            : card.vipRedeemedLastAt ?? null,

          unlimited,

          days_of_the_week,
          months,

          // ✅ NEW
          number_of_days,

          redemption: card.redemption,
          vipRedeemed: card.vipRedeemed,
        };
      });
    });
  }, [allCards]);

  // Filters (same as before, but now month+number_of_days window supported)
  const filteredOffers = useMemo(() => {
    const selected = norm(selectedRegion);

    return offerItems.filter((item) => {
      if (companyId && item.company?.id !== companyId && item.company?.clubId !== companyId)
        return false;

      if (selected) {
        const itemRegion = norm(item.company?.region || "");
        if (itemRegion !== selected) return false;
      }

      if (!isOfferValidForDate(item, selectedDate)) return false;

      if (onlyRedeemed && !(getOfferPlayStats(item).used > 0 || item.groupIsRedeemedOffer)) return false;

      return true;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offerItems, companyId, selectedRegion, selectedDate, onlyRedeemed]);

  //  Attach distances + sort (for Nearby tab)
  const offersWithDistance = useMemo<OfferItem[]>(() => {
    const uc = userCoords;
    if (!uc) return filteredOffers.map((x) => ({ ...x, distanceMiles: null }));

    return filteredOffers.map((x) => {
      const lat = x.company?.latitude;
      const lng = x.company?.longitude;
      if (lat == null || lng == null) return { ...x, distanceMiles: null };

      const miles = haversineMiles(uc.lat, uc.lng, lat, lng);
      return { ...x, distanceMiles: Number.isFinite(miles) ? miles : null };
    });
  }, [filteredOffers, userCoords]);

  const NEARBY_RADIUS_MILES = 3500;

  const nearbySortedOffers = useMemo<OfferItem[]>(() => {
    const copy = [...offersWithDistance];

    if (userCoords) {
      copy.splice(
        0,
        copy.length,
        ...copy.filter((x) => {
          const d = x.distanceMiles;
          return typeof d === "number" && Number.isFinite(d) && d <= NEARBY_RADIUS_MILES;
        })
      );
    } else {
      copy.splice(0, copy.length);
    }

    copy.sort((a, b) => (a.distanceMiles ?? 0) - (b.distanceMiles ?? 0));

    return copy;
  }, [offersWithDistance, userCoords]);

  const visibleOffers = activeTab === "nearby" ? nearbySortedOffers : filteredOffers;

  /* ------------------------------------------------------------------ */
  /* ✅ GROUPING (one card per groupId)                                   */
  /* ------------------------------------------------------------------ */

  const offerKey = (x: OfferItem) => `${x.hubdbRowId}-${x.offerNumber}`;

  const getAnchorDateForGrouping = (dateStr?: string) => {
    if (!dateStr) return new Date();
    const d = new Date(`${dateStr}T00:00:00`);
    return Number.isNaN(d.getTime()) ? new Date() : d;
  };

  const pickBestOfferFromGroup = (items: OfferItem[], anchorDate: Date) => {
    if (items.length === 1) return items[0];

    // 1) If group already redeemed, show the redeemed offer
    const redeemedKey =
      items.find((x) => x.groupRedeemedOfferKey)?.groupRedeemedOfferKey ?? null;

    if (redeemedKey) {
      const redeemed =
        items.find((x) => x.groupIsRedeemedOffer) ||
        items.find((x) => offerKey(x) === redeemedKey);
      if (redeemed) return redeemed;
    }

    // 2) Otherwise pick one valid for anchor date (selectedDate or today)
    const anchorIso = toISODate(anchorDate);
    const valid = items.filter((x) => isOfferValidForDate(x, anchorIso));
    const pool = valid.length > 0 ? valid : items;

    // 3) Tie-breakers: unlimited > most remaining > stable by offerNumber
    const scored = pool
      .map((x) => {
        const stats = getOfferPlayStats(x);
        const remainingScore = stats.isUnlimited ? Number.MAX_SAFE_INTEGER : (stats.remaining ?? 0);
        return { x, isUnlimited: stats.isUnlimited, remainingScore };
      })
      .sort((a, b) => {
        if (a.isUnlimited !== b.isUnlimited) return a.isUnlimited ? -1 : 1;
        if (b.remainingScore !== a.remainingScore) return b.remainingScore - a.remainingScore;
        return a.x.offerNumber - b.x.offerNumber;
      });

    return scored[0]?.x ?? items[0];
  };

  const groupOffersForDisplay = (items: OfferItem[], anchorDate: Date) => {
    // Group key = club + groupId (prevents collisions across courses)
    // Non-grouped offers keep unique keys.
    const map = new Map<string, OfferItem[]>();

    for (const item of items) {
      const k = item.groupId
        ? `${String(item.clubId).trim()}::${String(item.groupId).trim()}`
        : offerKey(item);

      const arr = map.get(k);
      if (arr) arr.push(item);
      else map.set(k, [item]);
    }

    return Array.from(map.values()).map((group) => pickBestOfferFromGroup(group, anchorDate));
  };

  const visibleGroupedOffers = useMemo(() => {
    const anchor = getAnchorDateForGrouping(selectedDate);
    const grouped = groupOffersForDisplay(visibleOffers, anchor);
    if (activeTab === "nearby") return grouped;
    // The HubDB table lists clubs in the order they were added, so sort by club name here.
    // Each club's offers keep their table order.
    return grouped
      .map((item, index) => ({ item, index }))
      .sort(
        (a, b) =>
          (a.item.company?.name || "").localeCompare(b.item.company?.name || "", undefined, { sensitivity: "base" }) ||
          a.index - b.index
      )
      .map(({ item }) => item);
  }, [visibleOffers, selectedDate, activeTab]);

  const totalOffersCount = visibleGroupedOffers.length;

  const openRedeemModal = (item: OfferItem) => {
    if (item.groupIsUnavailable) {
      setError(
        "This offer is unavailable because another offer in its exclusive group has already been redeemed."
      );
      return;
    }

    setError(null);
    setSelectedOffer(item);
    setModalOpen(true);
  };

  const confirmRedeem = async () => {
    if (!selectedOffer) return;
    if (modalBusy) return;
    if (isAdminPreview) return;

    // ✅ hard block
    if (selectedOffer.groupIsUnavailable) {
      setError(
        "This offer is unavailable because another offer in its exclusive group has already been redeemed."
      );
      return;
    }

    const validity = getOfferValidityNow(selectedOffer);
    if (!validity.isValidNow) {
      setError("This offer isn’t valid today.");
      return;
    }

    if (!contactId) {
      setError("Missing contactId (OTP session). Please log in again.");
      return;
    }
    if (!contact?.vipCardNumber) {
      setError("VIP Card number missing. Please log in again.");
      return;
    }

    const stats = getOfferPlayStats(selectedOffer);

    if (!stats.isUnlimited && (stats.remaining ?? 0) <= 0) {
      setError("No redemptions remaining for this offer.");
      return;
    }

    const key = `${selectedOffer.hubdbRowId}-${selectedOffer.offerNumber}`;
    setRedeemingKey(key);

    try {
      const resp = await redeemOffer({
        contactId,
        companyId: selectedOffer.company.id,
        clubId: selectedOffer.clubId,
        hubdbRowId: selectedOffer.hubdbRowId,
        offerNumber: selectedOffer.offerNumber,
        vipCardNumber: contact.vipCardNumber,
        totalAllowed: stats.allowed ?? 0,
        offerText: selectedOffer.offerText,
        groupId: selectedOffer.groupId ?? null,
      });

      const r = parseMaybeJson<any>(resp);
      if (!r?.success) {
        setError(r?.message || "Failed to redeem offer.");
        return;
      }

      const nowIso = new Date().toISOString();
      const redeemedOfferKey = `${selectedOffer.hubdbRowId}-${selectedOffer.offerNumber}`;

      // ✅ Update local UI counts + group locking
      setAllCards((prev) =>
        prev.map((card) => {
          const offers = Array.isArray(card.offers) ? card.offers : [];

          const nextOffers = offers.map((o, idx) => {
            if (!o || typeof o !== "object") return o;

            const oKey = `${String(card.hubdbRowId || "").trim()}-${idx + 1}`;

            const oGroupId = String((o as any).groupId ?? "").trim();
            const isSameGroup =
              !!selectedOffer.groupId && oGroupId === String(selectedOffer.groupId).trim();

            // If same group, lock everything except redeemedOfferKey
            if (isSameGroup) {
              if (oKey === redeemedOfferKey) {
                const currentUsed = Number(
                  (o as any).redeemedCount ?? (o as any).vipRedeemedCount ?? 0
                );
                return {
                  ...o,
                  redeemedCount: currentUsed + 1,
                  vipRedeemedLastAt: nowIso,

                  groupRedeemedOfferKey: redeemedOfferKey,
                  groupIsRedeemedOffer: true,
                  groupIsUnavailable: false,
                  groupRedeemedAt: nowIso,
                };
              }

              return {
                ...o,
                groupRedeemedOfferKey: redeemedOfferKey,
                groupIsRedeemedOffer: false,
                groupIsUnavailable: true,
                groupRedeemedAt: nowIso,
              };
            }

            // Not same group → only increment the exact offer object if it's the one we redeemed
            if (
              String(card.hubdbRowId || "").trim() === String(selectedOffer.hubdbRowId).trim() &&
              idx === Number(selectedOffer.offerNumber) - 1
            ) {
              const currentUsed = Number(
                (o as any).redeemedCount ?? (o as any).vipRedeemedCount ?? 0
              );
              return {
                ...o,
                redeemedCount: currentUsed + 1,
                vipRedeemedLastAt: nowIso,
              };
            }

            return o;
          });

          // Update card level only if the redeemed offer belongs to this card
          if (String(card.hubdbRowId || "").trim() !== String(selectedOffer.hubdbRowId).trim()) {
            return { ...card, offers: nextOffers };
          }

          const cardUsed = Number(card.redemption?.used ?? 0) || 0;
          const allowed = card.redemption?.totalAllowed;

          return {
            ...card,
            offers: nextOffers,
            redemption: card.redemption
              ? {
                  ...card.redemption,
                  used: cardUsed + 1,
                  remaining:
                    allowed == null ? null : Math.max(0, Number(allowed) - (cardUsed + 1)),
                }
              : card.redemption,
            vipRedeemedCount: Number(card.vipRedeemedCount ?? 0) + 1,
            vipRedeemedLastAt: nowIso,
          };
        })
      );

      setSelectedOffer((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          vipRedeemedCount: Number(prev.vipRedeemedCount ?? 0) + 1,
          vipRedeemedLastAt: nowIso,

          groupRedeemedOfferKey: selectedOffer.groupId
            ? redeemedOfferKey
            : prev.groupRedeemedOfferKey,
          groupIsRedeemedOffer: selectedOffer.groupId ? true : prev.groupIsRedeemedOffer,
          groupIsUnavailable: false,
          groupRedeemedAt: selectedOffer.groupId ? nowIso : prev.groupRedeemedAt,
        };
      });

      setModalOpen(false);
      setSelectedOffer(null);
    } catch (e) {
      console.error(e);
      setError("Redeem failed. Please try again.");
    } finally {
      setRedeemingKey(null);
    }
  };

  const closeModal = () => {
    if (modalBusy) return;
    setModalOpen(false);
    setSelectedOffer(null);
  };

  const logout = () => {
    setContact(null);
    setAppContactId(null);
    setModalOpen(false);
    setSelectedOffer(null);
    if (!isAppMode) {
      navigate("/offers");
    }
  };

  return (
    <section className="container my-5">
      {isAdminPreview && (
        <div
          className="d-flex flex-column flex-sm-row align-items-sm-center justify-content-between gap-2 mb-3 px-3 py-2"
          style={{ backgroundColor: "#fff8e6", border: "1px solid #e9d8a6", borderRadius: 8, fontSize: "0.85rem" }}
        >
          <span>
            <i className="bi bi-eye me-2" aria-hidden="true" />
            <strong>Admin preview:</strong> this is the offers page as {contact?.fullName || "this golfer"} sees it.
            Redeeming is turned off.
          </span>
          <div className="d-flex gap-2 flex-shrink-0">
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              onClick={() => setOnlyRedeemed(!onlyRedeemed)}
            >
              {onlyRedeemed ? "Show all offers" : "Show redeemed only"}
            </button>
            <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => window.close()}>
              Close preview
            </button>
          </div>
        </div>
      )}
      <div className="">
        <div className="card-body p-4">
          {/* Header */}
          <div className="bg-light py-5 px-3 mb-3">
            <div className="d-flex flex-column flex-sm-row align-items-stretch align-items-sm-center gap-3">
              {isLoggedIn ? (
                <div className="flex-grow-1">
                  <div className="small text-muted">
                    <h2
                      className="mb-1 text-uppercase"
                      style={{ fontSize: "1rem", letterSpacing: "0.08em" }}
                    >
                      <strong>{contact?.fullName || "Member"}</strong>
                    </h2>
                  </div>

                  <div className="small text-muted">
                    VIP Card: <strong>{contact?.vipCardNumber || "—"}</strong>
                  </div>

                  {contact?.membership?.expiresOn && (
                    <div className="small text-muted">Expires: {formatLongDate(contact.membership.expiresOn)}</div>
                  )}
                </div>
              ) : (
                <div className="flex-grow-1 text-center text-sm-start">
                  <div className="small text-muted">
                    <h2
                      className="mb-1 text-uppercase"
                      style={{ fontSize: "1rem", letterSpacing: "0.08em" }}
                    >
                      {loadingContact ? "Loading member..." : "Please log in to view VIP details"}
                    </h2>
                  </div>
                </div>
              )}

              <div className="d-grid d-sm-flex gap-2 flex-shrink-0">
                {isLoggedIn && contact?.membership?.renewSeason && (
                  <button
                    className="btn btn-primary btn-sm"
                    style={{ backgroundColor: "#8c6e2a", color: "#ffffff", border: "none" }}
                    onClick={openPurchasePageInNewTab}
                  >
                    Renew for {contact.membership.renewSeason}
                  </button>
                )}
                {isLoggedIn && !isAppMode && !isAdminPreview ? (
                  <button className="btn btn-outline-secondary btn-sm" onClick={logout}>
                    Logout
                  </button>
                ) : !isLoggedIn && !loadingContact ? (
                  <>
                    <button
                      className="btn btn-primary btn-sm"
                      style={{
                        backgroundColor: "#8c6e2a",
                        color: "#ffffff",
                        border: "none",
                        opacity: modalBusy ? 0.7 : 1,
                      }}
                      onClick={goToPurchasePage}
                    >
                      Purchase VIP Card
                    </button>
                    <button className="btn btn-primary btn-sm" onClick={goLogin}>
                      Login
                    </button>
                  </>
                ) : null}
              </div>
            </div>

            <div className="d-flex align-items-center justify-content-center justify-content-sm-start flex-wrap gap-2 mt-3">
              <span
                className="badge rounded-pill"
                style={{ backgroundColor: "#8c6e2a", color: "#fff", fontWeight: 600 }}
              >
                Total offers: {totalOffersCount}
              </span>
            </div>
          </div>

          {/* Filters */}
          <div className="row g-3">
            <div className="col-12 col-lg-4">
              <label className="form-label small">Filter By Club</label>
              <select
                id="company"
                className="form-select"
                value={companyId}
                onChange={(e) => setCompanyId(e.target.value)}
                disabled={loadingCompanies}
              >
                <option value="">{loadingCompanies ? "Loading clubs..." : "All clubs"}</option>
                {companies.map((c) => (
                  <option key={c.clubId} value={c.clubId}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-12 col-lg-4">
              <label className="form-label small">Filter By Region</label>
              <select
                className="form-select"
                value={selectedRegion}
                onChange={(e) => {
                  setSelectedRegion(e.target.value);
                  setCompanyId("");
                }}
                disabled={loadingCompanies || regions.length === 0}
              >
                <option value="">
                  {loadingCompanies
                    ? "Loading regions..."
                    : regions.length === 0
                    ? "No regions"
                    : "All regions"}
                </option>
                {regions.map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </select>
            </div>

            {/*  Custom Date Picker (works in all browsers) */}
            <div className="col-12 col-lg-4" ref={dateWrapRef}>
              <label className="form-label small">Filter by Date</label>

              <div className="position-relative">
                <input
                  type="text"
                  readOnly
                  className="form-control"
                  value={selectedDate ? prettyDate(selectedDate) : ""}
                  placeholder="Select a date"
                  onClick={openCalendar}
                  style={{
                    paddingRight: selectedDate ? "3.2rem" : undefined,
                    cursor: "pointer",
                  }}
                />

                {selectedDate && (
                  <button
                    type="button"
                    aria-label="Clear date"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setSelectedDate("");
                      setDateOpen(false);
                    }}
                    className="position-absolute top-50 end-0 translate-middle-y"
                    style={{
                      border: "none",
                      background: "transparent",
                      fontSize: "2rem",
                      fontWeight: 700,
                      lineHeight: 1,
                      padding: "0 0.9rem",
                      color: "#6c757d",
                      cursor: "pointer",
                    }}
                  >
                    ×
                  </button>
                )}

                {dateOpen && (
                  <div
                    className="position-absolute w-100 mt-2 bg-white border rounded shadow"
                    style={{ zIndex: 2000 }}
                  >
                    <div className="d-flex align-items-center justify-content-between px-3 py-2 border-bottom">
                      <button
                        className="btn btn-sm btn-outline-secondary"
                        type="button"
                        onClick={prevMonth}
                      >
                        ‹
                      </button>
                      <div className="fw-semibold">{monthLabel}</div>
                      <button
                        className="btn btn-sm btn-outline-secondary"
                        type="button"
                        onClick={nextMonth}
                      >
                        ›
                      </button>
                    </div>

                    <div className="px-3 py-2">
                      <div
                        className="d-grid"
                        style={{ gridTemplateColumns: "repeat(7, 1fr)", gap: "0.25rem" }}
                      >
                        {["S", "M", "T", "W", "T", "F", "S"].map((d) => (
                          <div
                            key={d}
                            className="text-center small text-muted"
                            style={{ fontWeight: 600 }}
                          >
                            {d}
                          </div>
                        ))}

                        {calendarDays.map((d) => {
                          const inMonth = d.getMonth() === calAnchorMonth.getMonth();
                          const selected = isSameDay(d, selectedDate);

                          return (
                            <button
                              key={d.toISOString()}
                              type="button"
                              onClick={() => pickDate(d)}
                              className="btn btn-sm"
                              style={{
                                padding: "0.45rem 0",
                                border: selected ? "1px solid #8c6e2a" : "1px solid transparent",
                                background: selected ? "rgba(140,110,42,0.12)" : "transparent",
                                color: inMonth ? "#212529" : "#adb5bd",
                                borderRadius: 8,
                              }}
                            >
                              {d.getDate()}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/*  Tabs (All vs Nearby) */}
          <ul className="nav nav-tabs mb-3 mt-3 justify-content-center gap-3">
            <li className="nav-item">
              <button
                className={`nav-link ${activeTab === "all" ? "active" : ""}`}
                type="button"
                onClick={() => setActiveTab("all")}
              >
                All Offers
              </button>
            </li>
            <li className="nav-item">
              <button
                className={`nav-link ${activeTab === "nearby" ? "active" : ""}`}
                type="button"
                onClick={() => {
                  setActiveTab("nearby");
                  requestLocation();
                }}
              >
                Nearby Offers
              </button>
            </li>
          </ul>

          {/*  Nearby tab helpers */}
          {activeTab === "nearby" && (!userCoords || geoLoading || geoError) && (
            <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
              <div className="small text-muted">
                {geoLoading ? "Getting your location…" : !userCoords ? "Enable location to sort offers by distance." : null}
              </div>

              {!userCoords && (
                <div className="d-flex align-items-center gap-2">
                  <button
                    type="button"
                    className="btn btn-outline-secondary btn-sm"
                    onClick={requestLocation}
                    disabled={geoLoading}
                  >
                    {geoLoading ? "Locating…" : "Use My Location"}
                  </button>
                </div>
              )}

              {geoError && <div className="text-danger small w-100">{geoError}</div>}
            </div>
          )}

          {loadingOffers && <div className="text-muted">Loading offers…</div>}
          {!loadingOffers && visibleGroupedOffers.length === 0 && (
            <div className="text-muted">No offers found for the selected filters.</div>
          )}

          {/* Cards */}
          <div className="row g-5">
            {visibleGroupedOffers.map((item) => {
              const key = `${item.hubdbRowId}-${item.offerNumber}`;
              const isBusy = redeemingKey === key;

              const stats = getOfferPlayStats(item);

              // ✅ FIX: Unavailable = groupIsUnavailable ONLY
              const isUnavailable = Boolean(item.groupIsUnavailable);

              // ✅ FIX: Redeemed = plays exhausted ONLY (do not use groupIsRedeemedOffer here)
              const isRedeemed = !stats.isUnlimited && (stats.remaining ?? 0) <= 0;

              const locationText = formatLocation(item.company);
              const phoneText = formatUSPhone(item.company?.phone);

              const miles =
                activeTab === "nearby" && typeof item.distanceMiles === "number"
                  ? `${item.distanceMiles.toFixed(item.distanceMiles < 10 ? 1 : 0)} mi`
                  : null;

              return (
                <div className="col-12 col-lg-6" key={`${item.hubdbRowId}-${item.offerNumber}`}>
                  <div className="card h-100 shadow-sm border-0">
                    <div className="px-3 py-2" style={{ backgroundColor: "#023822" }}>
                      <div className="d-flex justify-content-between align-items-start gap-2">
                        <div className="fw-semibold text-white">{item.company?.name || "Course"}</div>
                        {miles && (
                          <div className="small" style={{ color: "rgba(255,255,255,0.85)" }}>
                            {miles}
                          </div>
                        )}
                      </div>

                      {item.company?.region && (
                        <div className="small text-white-50">{item.company.region}</div>
                      )}
                      {locationText && <div className="small text-white-50">{locationText}</div>}
                      {phoneText && <div className="small text-white-50">{phoneText}</div>}

                      {activeTab === "nearby" && !miles && (
                        <div className="small text-white-50">
                          {userCoords ? "Distance unavailable (missing lat/long)." : ""}
                        </div>
                      )}

                      {isUnavailable && (
                        <div className="small" style={{ color: "rgba(255,255,255,0.85)" }}>
                          Unavailable (exclusive offer already used)
                        </div>
                      )}
                    </div>

                    <div className="card-body">
                      <div style={{ whiteSpace: "pre-wrap" }}>{item.offerText}</div>

                      <div className="d-flex justify-content-center mt-3">
                        {isUnavailable ? (
                          <div className="fw-semibold text-muted">Unavailable</div>
                        ) : isRedeemed ? (
                          <div className="fw-semibold text-muted">
                            Redeemed ({stats.used}/{stats.allowed})
                          </div>
                        ) : (
                          <button
                            type="button"
                            className="p-0 bg-transparent border-0 fw-semibold"
                            style={{
                              color: "#8c6e2a",
                              cursor: isBusy ? "default" : "pointer",
                            }}
                            onClick={() => openRedeemModal(item)}
                            disabled={isBusy}
                          >
                            {isBusy ? "Redeeming…" : `View Offer`}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {error && !isAppMode && <div className="text-danger mt-3">{error}</div>}
        </div>
      </div>

      {/* Modal */}
      {modalOpen && selectedOffer && (
        <>
          <div
            className="position-fixed top-0 start-0 w-100 h-100"
            style={{ background: "rgba(0,0,0,0.5)", zIndex: 1040 }}
            onClick={modalBusy ? undefined : closeModal}
          />

          <div
            className="position-fixed top-50 start-50 translate-middle w-100"
            style={{ maxWidth: 640, zIndex: 1050 }}
          >
            <div className="card shadow border-0">
              <div
                className="px-4 py-3"
                style={{
                  backgroundColor: "#023822",
                  color: "#fff",
                  borderTopLeftRadius: 6,
                  borderTopRightRadius: 6,
                }}
              >
                <div className="d-flex justify-content-between align-items-start gap-3">
                  <div>
                    <h5 className="mb-1" style={{ color: "#ffffff" }}>
                      {selectedOffer.company.name}
                    </h5>

                    {selectedOffer.company.region && (
                      <div className="small text-white-50">{selectedOffer.company.region}</div>
                    )}

                    <div className="small text-white-50">
                      {formatLocation(selectedOffer.company) || ""}
                    </div>

                    <div className="small text-white-50">
                      {selectedOffer.company.phone
                        ? formatUSPhone(selectedOffer.company.phone)
                        : ""}
                    </div>

                    {selectedOffer.groupIsUnavailable && (
                      <div className="alert alert-secondary py-2 mt-3 mb-0 text-center">
                        This offer is unavailable because another offer in its exclusive group has already been redeemed.
                      </div>
                    )}
                  </div>

                  <button
                    className="btn btn-sm border-0"
                    onClick={closeModal}
                    disabled={modalBusy}
                    style={{
                      color: "#fff",
                      opacity: 0.85,
                      background: "transparent",
                      lineHeight: 1,
                      fontSize: "1.15rem",
                    }}
                    aria-label="Close"
                  >
                    ✕
                  </button>
                </div>
              </div>

              <div className="card-body p-4">
                <div style={{ whiteSpace: "pre-wrap" }}>{selectedOffer.offerText}</div>

                {isLoggedIn &&
                  (() => {
                    const v = getOfferValidityNow(selectedOffer);
                    if (!v.isValidNow) return null;

                    const stats = getOfferPlayStats(selectedOffer);

                    if (stats.isUnlimited) {
                      return (
                        <div className="mt-3">
                          <div className="small text-muted text-center">
                            <strong>Unlimited plays</strong>
                          </div>
                          <div className="small text-muted text-center">
                            Used: <strong>{stats.used}</strong>
                          </div>
                        </div>
                      );
                    }

                    return (
                      <div className="mt-3">
                        <div className="small text-muted text-center">
                          <strong>Used:</strong> {stats.used} of {stats.allowed}
                        </div>

                        {(stats.remaining ?? 0) <= 0 && (
                          <div className="alert alert-secondary py-2 mt-3 mb-0 text-center">
                            No redemptions remaining for this offer.
                          </div>
                        )}
                      </div>
                    );
                  })()}

                {(() => {
                  const v = getOfferValidityNow(selectedOffer);
                  if (v.isValidNow) return null;

                  const dayMsg =
                    v.allowedDays.length > 0 ? `Valid days: ${v.allowedDays.join(", ")}` : "";

                  // ✅ Update message: show months + number_of_days range (still displays months list)
                  const monthMsg =
                    v.allowedMonths.length > 0 ? `Valid months: ${v.allowedMonths.join(", ")}` : "";

                  return (
                    <div className="alert alert-secondary py-2 mt-3 mb-0 text-center">
                      <div className="fw-semibold">This offer isn’t valid today.</div>
                      {(dayMsg || monthMsg) && (
                        <>
                          <div className="small">{dayMsg}</div>
                          <div className="small">{monthMsg}</div>
                        </>
                      )}
                    </div>
                  );
                })()}

                <div className="d-flex align-items-center justify-content-center gap-2 mt-3">
                  <img src="/assets/tag.gif" className="img img-fluid" />
                </div>

                {modalBusy && (
                  <div className="d-flex align-items-center justify-content-center gap-2 mt-3">
                    <div
                      className="spinner-border spinner-border-sm"
                      role="status"
                      aria-label="Saving"
                    />
                    <span className="small text-muted">Processing redemption</span>
                  </div>
                )}

                <div className="d-flex justify-content-center gap-3 mt-4 flex-wrap">
                  {isLoggedIn ? (
                    (() => {
                      const stats = getOfferPlayStats(selectedOffer);
                      const validity = getOfferValidityNow(selectedOffer);
                      const noRemaining = !stats.isUnlimited && (stats.remaining ?? 0) <= 0;

                      if (!validity.isValidNow) return null;

                      const disableRedeem =
                        modalBusy || noRemaining || Boolean(selectedOffer.groupIsUnavailable) || isAdminPreview;

                      return (
                        <button
                          className="btn fw-semibold"
                          onClick={confirmRedeem}
                          disabled={disableRedeem}
                          style={{
                            backgroundColor: "#8c6e2a",
                            color: "#ffffff",
                            border: "none",
                            opacity: disableRedeem ? 0.7 : 1,
                          }}
                        >
                          {selectedOffer.groupIsUnavailable
                            ? "Unavailable"
                            : modalBusy
                            ? "Saving…"
                            : noRemaining
                            ? "No Plays Remaining"
                            : isAdminPreview
                            ? "Redeem Now (off in preview)"
                            : "Redeem Now"}
                        </button>
                      );
                    })()
                  ) : isAppMode && !isLoggedIn ? (
                    null
                  ) : (
                    <>
                      <button
                        className="btn btn-primary fw-semibold"
                        onClick={goToPurchasePage}
                        disabled={modalBusy}
                        style={{
                          backgroundColor: "#8c6e2a",
                          color: "#ffffff",
                          border: "none",
                          opacity: modalBusy ? 0.7 : 1,
                        }}
                      >
                        Purchase VIP Card
                      </button>

                      <button
                        className="btn btn-primary fw-semibold"
                        onClick={goLogin}
                        disabled={modalBusy}
                      >
                        Login
                      </button>
                    </>
                  )}
                </div>

                {isLoggedIn && (
                  <div className="small text-muted mt-3 text-center">
                    Club Official Must Redeem
                  </div>
                )}

                {error && <div className="text-danger mt-3 text-center">{error}</div>}
              </div>
            </div>
          </div>
        </>
      )}
    </section>
  );
};

export default OffersList;
